import {
  Article,
  ArticleIdNbrArticles,
  ArticleWithMediaCount,
  EdsData,
  SalesStopData,
} from "@coworker/app/src/components/RoomSettings/types/article";
import { Media } from "@coworker/app/src/components/RoomSettings/types/media";
import { ItemListViewItemCard } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/ItemListViewItemCard/ItemListViewItemCard";
import { getMediaItemBadges } from "@coworker/apprestructured/src/media-maintenance/helpers/getMediaItemBadges";
import styles from "@coworker/apprestructured/src/media-maintenance/styles/common-media-styles.module.css";
import { MediaItemType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemType";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import { ItemStockInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemStock";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { useTranslation } from "@coworker/locales";
import { useMemo } from "react";

interface ItemsListViewItemProps {
  article: Article | ArticleWithMediaCount;
  media: Media;
  hasOngoingTasks: boolean;
  salesStopData?: SalesStopData | null;
  edsData?: EdsData | null;
  itemCardRenderType?: ItemCardRenderType;
  mediaItemType?: MediaItemType;
  selectArticleIdsMode?: boolean;
  isSelected?: boolean;
  handleSelectedItems?: (articleIdNbrArticles: ArticleIdNbrArticles) => void;
  itemInfo: Item | null;
  itemInfoLoading: boolean;
  badges4AK1?: string[];
  itemTags?: string[];
  stockInfo?: ItemStockInfo;
}

export const ItemListViewItem = ({
  article,
  media,
  hasOngoingTasks,
  salesStopData,
  edsData,
  itemCardRenderType = ItemCardRenderType.static,
  mediaItemType = MediaItemType.REGULAR_ITEM,
  selectArticleIdsMode = false,
  isSelected = false,
  handleSelectedItems = (_articleIdNbrArticles: ArticleIdNbrArticles) => {},
  itemInfo,
  itemInfoLoading,
  badges4AK1 = [],
  itemTags = [],
  stockInfo,
}: ItemsListViewItemProps) => {
  const { t } = useTranslation();

  const handleSelectedItemChange = () => {
    handleSelectedItems({
      articleId: article.id,
      nbrArticles: article?.nbrArticles || 0,
    });
  };

  const badges = useMemo(() => {
    const shouldShowProductBadges = false;
    return getMediaItemBadges(
      article.id,
      [article.id],
      t,
      hasOngoingTasks,
      salesStopData,
      media,
      shouldShowProductBadges,
      badges4AK1,
      itemTags
    );
  }, [
    article.id,
    badges4AK1,
    hasOngoingTasks,
    itemTags,
    media,
    salesStopData,
    t,
  ]);

  return (
    <div
      className={
        selectArticleIdsMode
          ? styles["item-card-root-row"]
          : styles["list-view-item-root"]
      }
    >
      <div
        className={
          selectArticleIdsMode
            ? styles["item-card-root-column"]
            : styles["list-view-item-root"]
        }
      >
        <ItemListViewItemCard<Article>
          mediaItemType={mediaItemType}
          itemCardRenderType={itemCardRenderType}
          edsData={edsData ?? null}
          isItemInGroups={false}
          badges={badges || []}
          article={article}
          media={media}
          mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
          showActions={!selectArticleIdsMode}
          itemInfo={itemInfo}
          itemInfoLoading={itemInfoLoading}
          stockInfo={stockInfo}
        />
      </div>

      {selectArticleIdsMode && !article.parentArticleCombinationNumber && (
        <div
          className={styles["select-item-checkbox"]}
          data-testid={`select-article-checkbox-aggregated`}
        >
          <FixaCheckbox
            id={article.id}
            value={article.id}
            checked={isSelected || false}
            onChange={handleSelectedItemChange}
          />
        </div>
      )}
    </div>
  );
};
